import { useEffect, useState } from 'react';

/**
 * `useMediaQuery('(min-width: 768px)')` — reactive boolean that flips
 * when the viewport crosses the breakpoint. Used by Sheet, BottomNav,
 * Sidebar, and the per-resource list/table chooser to make
 * presentational decisions in JS where Tailwind's `md:` modifier isn't
 * enough (typically when the same component renders entirely
 * different DOM shapes for mobile vs desktop).
 *
 * The first paint matches `false` on SSR; on the client it
 * synchronously reads `matchMedia(...).matches` so the second paint
 * is correct without flicker for purely-CSS hidden cases. Components
 * that change DOM structure should use a `mounted` guard to avoid the
 * brief flash.
 */
export function useMediaQuery(query: string): boolean {
  const get = () =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Convenience: mobile-first breakpoint matching Tailwind's `md`. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}
